import { type ReactNode, useCallback, useMemo, useState } from 'react';

import {
  type ExperienceMode,
  PERSONA_LENS_META,
  type PersonaLens,
  PORTAL_GLOSSARY,
} from './glossary';
import {
  inferDefaultLensFromEmail,
  PortalExperienceContext,
  type PortalExperienceContextValue,
} from './PortalExperienceContext';

const STORAGE_MODE_KEY = 'giyus.portal.experience.mode.v2';
const STORAGE_LENS_KEY = 'giyus.portal.experience.lens.v2';
const USER_SPECIALTY_PREFIX = 'giyus.portal.userSpecialty.v2.';
const USER_MODE_PREFIX = 'giyus.portal.userMode.v2.';

function isValidLens(raw: string | null): raw is PersonaLens {
  return (
    raw === 'clinical' ||
    raw === 'operations' ||
    raw === 'financial' ||
    raw === 'external' ||
    raw === 'admin'
  );
}

function readStoredModeForUser(
  userEmail: string | undefined,
  defaultMode: ExperienceMode,
): ExperienceMode {
  try {
    if (userEmail) {
      const userRaw = window.localStorage.getItem(
        `${USER_MODE_PREFIX}${userEmail.trim().toLowerCase()}`,
      );
      if (userRaw === 'plain' || userRaw === 'expert') {
        return userRaw;
      }
    }
    const raw = window.localStorage.getItem(STORAGE_MODE_KEY);
    if (raw === 'plain' || raw === 'expert') {
      return raw;
    }
  } catch {
    // Ignore storage errors in restricted environments
  }
  return defaultMode;
}

function readStoredLensForUser(
  userEmail: string | undefined,
  defaultLens: PersonaLens,
): PersonaLens {
  try {
    if (userEmail) {
      const userRaw = window.localStorage.getItem(
        `${USER_SPECIALTY_PREFIX}${userEmail.trim().toLowerCase()}`,
      );
      if (isValidLens(userRaw)) {
        return userRaw;
      }
    }
    const raw = window.localStorage.getItem(STORAGE_LENS_KEY);
    if (isValidLens(raw)) {
      return raw;
    }
  } catch {
    // Ignore storage errors
  }
  return defaultLens;
}

export interface PortalExperienceProviderProps {
  readonly userEmail?: string;
  readonly children: ReactNode;
}

export function PortalExperienceProvider({ userEmail, children }: PortalExperienceProviderProps) {
  const inferredLens = inferDefaultLensFromEmail(userEmail);
  const [personaLens, setPersonaLensState] = useState<PersonaLens>(() =>
    readStoredLensForUser(userEmail, inferredLens),
  );
  const [mode, setModeState] = useState<ExperienceMode>(() =>
    readStoredModeForUser(userEmail, PERSONA_LENS_META[inferredLens].defaultMode),
  );

  // Sync specialty & explanation mode whenever the authenticated user profile changes
  const [previousInput1, setPreviousInput1] = useState<readonly unknown[] | null>(null);
  if (previousInput1 === null || previousInput1[0] !== userEmail) {
    setPreviousInput1([userEmail]);
    const reconcile = () => {
      const nextInferred = inferDefaultLensFromEmail(userEmail);
      const nextLens = readStoredLensForUser(userEmail, nextInferred);
      const nextMode = readStoredModeForUser(userEmail, PERSONA_LENS_META[nextLens].defaultMode);
      setPersonaLensState(nextLens);
      setModeState(nextMode);
    };
    reconcile();
  }

  const setMode = useCallback(
    (next: ExperienceMode) => {
      setModeState(next);
      try {
        window.localStorage.setItem(STORAGE_MODE_KEY, next);
        if (userEmail) {
          window.localStorage.setItem(`${USER_MODE_PREFIX}${userEmail.trim().toLowerCase()}`, next);
        }
      } catch {
        // Ignore storage errors
      }
    },
    [userEmail],
  );

  const setPersonaLens = useCallback(
    (lens: PersonaLens) => {
      setPersonaLensState(lens);
      const recommendedMode = PERSONA_LENS_META[lens].defaultMode;
      setModeState(recommendedMode);
      try {
        window.localStorage.setItem(STORAGE_LENS_KEY, lens);
        window.localStorage.setItem(STORAGE_MODE_KEY, recommendedMode);
        if (userEmail) {
          const normalized = userEmail.trim().toLowerCase();
          window.localStorage.setItem(`${USER_SPECIALTY_PREFIX}${normalized}`, lens);
          window.localStorage.setItem(`${USER_MODE_PREFIX}${normalized}`, recommendedMode);
        }
      } catch {
        // Ignore storage errors
      }
    },
    [userEmail],
  );

  const value = useMemo<PortalExperienceContextValue>(
    () => ({
      mode,
      isPlainMode: mode === 'plain',
      isExpertMode: mode === 'expert',
      personaLens,
      setMode,
      toggleMode: () => {
        setMode(mode === 'plain' ? 'expert' : 'plain');
      },
      setPersonaLens,
      getTerm: (key: string) => PORTAL_GLOSSARY[key],
      formatLabel: (plainText: string, expertText: string) =>
        mode === 'plain' ? plainText : expertText,
    }),
    [mode, personaLens, setMode, setPersonaLens],
  );

  return (
    <PortalExperienceContext.Provider value={value}>{children}</PortalExperienceContext.Provider>
  );
}
